import React, { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Eye, LogOut, Lock } from "lucide-react";
import { toast } from "sonner";

export default function ImpersonationBanner() {
  const { isImpersonating, user, impersonatorEmail, impersonationReadonly, stopImpersonation } = useAuth();
  const [exiting, setExiting] = useState(false);

  if (!isImpersonating || !user) return null;
  const readonly = impersonationReadonly;

  const onExit = async () => {
    setExiting(true);
    try {
      await stopImpersonation();
      toast.success("Returned to your admin session.");
      // Full page load, not a route change: every panel refetches as the admin
      // and nothing of the member's view survives in memory.
      window.location.replace(readonly ? "/admin/partners/users" : "/dashboard");
    } catch {
      toast.error("Could not exit impersonation cleanly. Please log in again.");
      setExiting(false);
    }
  };

  return (
    <div
      data-testid="impersonation-banner"
      className="sticky top-0 z-50 w-full text-white"
      style={{
        background: "linear-gradient(90deg, #B91C1C 0%, #DC2626 50%, #B91C1C 100%)",
        boxShadow: "0 6px 24px rgba(220,38,38,0.30)",
      }}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-12 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-white/15 shrink-0">
            <Eye size={14} />
          </span>
          <div className="text-sm leading-tight truncate">
            <span className="font-semibold">Viewing as</span>{" "}
            <span className="font-mono opacity-90" data-testid="impersonation-target-email">
              {user.email}
            </span>
            <span className="hidden md:inline opacity-80">
              {" "}· {user.name || "—"} · {user.role}
            </span>
            <span className="hidden lg:inline opacity-70">
              {" "}· admin: <span className="font-mono">{impersonatorEmail}</span>
            </span>
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <span className="hidden md:inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-full bg-white/15" data-testid="impersonation-mode">
            <Lock size={11} /> {readonly ? "Read-only · nothing can be saved" : "Full access · actions logged"}
          </span>
          <button
            type="button"
            onClick={onExit}
            disabled={exiting}
            data-testid="impersonation-exit"
            className="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-full bg-white text-red-700 hover:bg-white/95 disabled:opacity-60 transition-colors"
          >
            <LogOut size={14} /> {exiting ? "Exiting…" : "Exit impersonation"}
          </button>
        </div>
      </div>
    </div>
  );
}
